import { IContentModel } from '@shared/models/content.model';
import { IAdvancedTemplateFooterModel } from '../advanced-templates-service/advanced-templates.model';
import { RecipientsFilterModel } from '@routes/campaigns/models/campaign-model';
import { ContactType } from '@shared/enums/ContactType';
import { FromType } from '@shared/enums/FromType';
import { MailjetStatusType } from '@shared/enums/MailjetStatusType';
import { RecipientStatusType } from '@shared/enums/RecipientStatusType';
import { ReplyToType } from '@shared/enums/ReplyToType';
import { SenderType } from '@shared/enums/SenderType';
import { IDropdownModelString } from '@shared/models/dropdown-string-model';
import { IContentLanguage } from '@shared/models/multilanguage-model';
import { IMailStatus } from '../../legacy/mailstatuses-service/mailstatuses-model';
import { IInvitationExportDetailContact } from '../../legacy/invitations-service/invitations-model';
import { IUnsubscribeSettingsOption } from '../../legacy/settings-service/setting-model';
import { SortedPage } from '@shared/models/sortedPage';
import { ILanguage } from '../../legacy/languages-service/languages-model';

export interface IMailingCampaignItemList {
    campaigns: IMailingCampaignItem[];
}

export interface IMailingCampaignItem {
    id: string;
    text: string;
}

export interface ICampaignDesign {
    campaignId: string;
    advancedTemplateId: string;
    bannerContents: IContentModel[];
    footers: IAdvancedTemplateFooterModel[];
}

export class CampaignDesign implements ICampaignDesign {
    constructor(
        public campaignId: string,
        public advancedTemplateId: string,
        public bannerContents: IContentModel[],
        public footers: IAdvancedTemplateFooterModel[]
    ) { }
}

export class CampaignsFilterModel {
    // The campaign owner filter
    public ownerId: string = null;
    // The campaign filter dropdown
    public campaignsFilter = 0;
    // The campaign search value
    public searchValue = '';
    // The campaign sort filter
    public campaignsSortFilter = 0;
    // The campaign tags filter
    public tagIds = [];
}

export class CampaignsPage extends SortedPage {
    public filter: CampaignsFilterModel = new CampaignsFilterModel();
}

export class CampaignsPagedData<T> {
    public campaigns = new Array<T>();
    public totalElements: number;
    public totalPages: number;
}

// Campaigns
export interface ICampaignForList {
    id: string;
    name: string;
    owner: string;
    sendDate: Date;
    endDate: Date;
    isDraft: boolean;
    mailStatuses: IMailStatus[];
    mailStatistics: ICampaignMailStatistics;
}
export interface ICampaign {
    id: string;
    name: string;
    ownerId: string;
    owner: ICampaignOwner;
    isDraft: boolean;
    isActive: boolean;
    isDeleted: boolean;
    isProcessed: boolean;
    sendDate: Date;
    endDate: Date;
    advancedTemplate: ICampaignAdvancedTemplate;
    contact: ICampaignContact;
    senderSetting: ICampaignSenderSetting;
    bannerContents: IContentModel[];
    languages: ILanguage[];
    mail: ICampaignMail;
    allowedListOfRecipients: IAllowedListOfRecipients;
    allowSync: boolean;
    unsubscribeOption: IUnsubscribeOption;
    tags: ICampaignTag[];
    footers: IAdvancedTemplateFooterModel[];
}

export class Campaign implements ICampaign {
    constructor(
        public id: string,
        public name: string,
        public ownerId: string,
        public owner: ICampaignOwner,
        public isDraft: boolean,
        public isActive: boolean,
        public isDeleted: boolean,
        public isProcessed: boolean,
        public sendDate: Date,
        public endDate: Date,
        public advancedTemplate: ICampaignAdvancedTemplate,
        public contact: ICampaignContact,
        public senderSetting: ICampaignSenderSetting,
        public bannerContents: IContentModel[],
        public languages: ILanguage[],
        public mail: ICampaignMail,
        public allowedListOfRecipients: IAllowedListOfRecipients,
        public allowSync: boolean,
        public unsubscribeOption: IUnsubscribeOption,
        public tags: ICampaignTag[],
        public footers: IAdvancedTemplateFooterModel[]
    ) { }
}

export interface ICampaignAdvancedTemplate {
    id: string;
    name: string;
}

export class CampaignAdvancedTemplate implements ICampaignAdvancedTemplate {
    constructor(
        public id: string,
        public name: string
    ) { }
}

export interface ICampaignOwner {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
}

export class CampaignOwner implements ICampaignOwner {
    constructor(
        public firstName: string,
        public lastName: string,
        public email: string,
        public mobile: string
    ) { }
}

export interface ICampaignContact {
    name: string;
    jobTitle: string;
    email: string;
    mobile: string;
}

export class CampaignContact implements ICampaignContact {
    constructor(
        public name: string,
        public jobTitle: string,
        public email: string,
        public mobile: string
    ) { }
}

export interface ICampaignSenderSetting {
    fromType: FromType;
    fromName: string;
    fromEmail: string;
    replyToType: ReplyToType;
    replyToEmail: string;
}

export class CampaignSenderSetting implements ICampaignSenderSetting {
    constructor(
        public fromType: FromType,
        public fromName: string,
        public fromEmail: string,
        public replyToType: ReplyToType,
        public replyToEmail: string
    ) { }
}

export class CampaignSenderSettingVm {
    private constructor(
        public fromType: FromType,
        public fromName: string,
        public fromEmail: string,
        public replyToType: ReplyToType,
        public replyToEmail: string
    ) {

    }

    public static getDomain(email: string) {
        const splitted = email.split('@');
        if (splitted.length === 2) {
            const host = splitted[0];
            const domain = splitted[1];
            return domain;
        }
        return '';
    }

    public static create(entity: ICampaignSenderSetting): CampaignSenderSettingVm {
        return new CampaignSenderSettingVm(entity.fromType, entity.fromName, entity.fromEmail, entity.replyToType, entity.replyToEmail);
    }

    public getEntity(): ICampaignSenderSetting {
        // we assume fromEmail always has valid email format
        return new CampaignSenderSetting(this.fromType, this.fromName, this.fromEmail, this.replyToType, this.replyToEmail);
    }
}

export interface ICampaignClone {
    id: string;
    name: string;
    sendDate: Date;
    endDate: Date;
    cloneMail: boolean;
    cloneSenders: boolean;
    cloneRecipients: boolean;
}

export class CampaignClone implements ICampaignClone {
    constructor(
        public id: string,
        public name: string,
        public sendDate: Date,
        public endDate: Date,
        public cloneMail: boolean,
        public cloneSenders: boolean,
        public cloneRecipients: boolean
    ) { }
}

export interface ICampaignSenderIds {
    ids: string[];
}

export class CampaignSenderIds implements ICampaignSenderIds {
    constructor(
        public ids: string[]
    ) { }
}

export interface ICampaignSendersForDropdown {
    senders: IDropdownModelString[];
}

export interface ICampaignRecipientsForList {
    id: string;
    senderId: string;
    contactId: string;
    contactFirstName: string;
    contactLastName: string;
    contactCompanyName: string;
    senderName: string;
    statusType: RecipientStatusType;
    mailjetStatus: MailjetStatusType;
    sendDate: Date;
}

export interface ICampaignRecipientsExport {
    recipients: ICampaignRecipientsExportDetail[];
}

export interface ICampaignRecipientsExportDetail {
    senderId: string;
    senderName: string;
    campaignName: string;
    statusType: RecipientStatusType;
    mailjetStatus: MailjetStatusType;
    modified: string;
    contact: IInvitationExportDetailContact;
}

export interface ICampaignRecipientsPrint {
    recipients: ICampaignRecipientsPrintDetail[];
}

export interface ICampaignRecipientsPrintDetail {
    senderId: string;
    contactId: string;
    campaignName: string;
    contactName: string;
    contactCompanyName: string;
    senderName: string;
    inviteeJobTitle: string;
    statusType: RecipientStatusType;
}

export interface ICampaignMailStatistics {
    totalMails: number;
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    totalUnsub: number;
    totalSpam: number;
    totalBlocked: number;
    totalBounced: number;
    totalQueued: number;
    totalNotProcessed: number;
    totalDelivered: number;
}

export class CampaignMailStatistics implements ICampaignMailStatistics {
    constructor(
        public totalMails: number,
        public totalSent: number,
        public totalOpened: number,
        public totalClicked: number,
        public totalUnsub: number,
        public totalSpam: number,
        public totalBlocked: number,
        public totalBounced: number,
        public totalQueued: number,
        public totalNotProcessed: number,
        public totalDelivered: number
    ) { }
}

export interface ICampaignSenders {
    senders: ICampaignSender[];
}

export interface ICampaignSender {
    id: string;
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface ICampaignMail {
    id: string;
    showLogo: boolean;
    showBanner: boolean;
    enableBcc: boolean;
    contents: IContentLanguage[];
    subjects: IContentLanguage[];
    currentAttachements: string[];
    attachements: File[];
}

export class CampaignMail implements ICampaignMail {
    constructor(
        public id: string,
        public showLogo: boolean,
        public showBanner: boolean,
        public enableBcc: boolean,
        public contents: IContentLanguage[],
        public subjects: IContentLanguage[],
        public currentAttachements: string[],
        public attachements: File[]
    ) { }
}

export interface ICampaignMailPreview extends ICampaignMail {
    languageId: string;
    userIds: string[];
}

export class CampaignMailPreview implements ICampaignMailPreview {
    constructor(
        public id: string,
        public showLogo: boolean,
        public showBanner: boolean,
        public enableBcc: boolean,
        public contents: IContentLanguage[],
        public subjects: IContentLanguage[],
        public currentAttachements: string[],
        public attachements: File[],
        public languageId: string,
        public userIds: string[]
    ) { }
}

export interface ICampaignRecipientContacts {
    senderId: string;
    senderType: SenderType;
    contactIds: string[];
}

export class CampaignRecipientContacts implements ICampaignRecipientContacts {
    constructor(
        public senderId: string,
        public senderType: SenderType,
        public contactIds: string[]
    ) { }
}

export interface ICampaignRecipientContact {
    senderId: string;
    firstName: string;
    lastName: string;
    title: string;
    type: ContactType;
    genderId: string;
    languageId: string;
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    salutation: string;
    mailPermission: boolean;
    smsPermission: boolean;
    accountOwnerId: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
    country: string;
    crmId: string;
    customerNumber: string;
}

export class CampaignRecipientContact implements ICampaignRecipientContact {
    constructor(
        public senderId: string,
        public firstName: string,
        public lastName: string,
        public title: string,
        public type: ContactType,
        public genderId: string,
        public languageId: string,
        public email: string,
        public phone: string,
        public mobile: string,
        public companyName: string,
        public salutation: string,
        public mailPermission: boolean,
        public smsPermission: boolean,
        public accountOwnerId: string,
        public addressLineOne: string,
        public addressLineTwo: string,
        public city: string,
        public postalCode: string,
        public country: string,
        public crmId: string,
        public customerNumber: string
    ) { }
}

export interface ICampaignRecipientIds {
    ids: string[];
    isSelectAll: boolean;
    filterModel: RecipientsFilterModel;
    senderId: string;
    senderType: SenderType;
}

export class CampaignRecipientIds implements ICampaignRecipientIds {
    constructor(
        public ids: string[],
        public isSelectAll: boolean,
        public filterModel: RecipientsFilterModel,
        public senderId: string,
        public senderType: SenderType
    ) { }
}

export interface IExportRecipientsSettings {
    fileName: string;
    canExportFullName: boolean;
    canExportCampaignName: boolean;
    canExportSenderName: boolean;
    canExportStatusType: boolean;
    canExportModified: boolean;
    canExportContactFirstName: boolean;
    canExportContactLastNamePrefix: boolean;
    canExportContactLastName: boolean;
    canExportContactEmail: boolean;
    canExportContactMobile: boolean;
    canExportContactPhone: boolean;
    canExportContactJobTitle: boolean;
    canExportContactCompany: boolean;
    canExportContactAccountOwner: boolean;
    canExportContactLanguage: boolean;
    canExportContactContactType: boolean;
    canExportContactGender: boolean;
    canExportContactSalutation: boolean;
    canExportContactCrmId: boolean;
    canExportContactCustomerNumber: boolean;
    canExportContactAccessCode: boolean;
    canExportContactSmsPermission: boolean;
    canExportContactIsActive: boolean;
    canExportContactAddressLineOne: boolean;
    canExportContactAddressLineTwo: boolean;
    canExportContactCity: boolean;
    canExportContactPostalCode: boolean;
    canExportContactCountry: boolean;
    canExportContactCustomFields: string[];
    canExportContactUnsubscribeOptions: IUnsubscribeSettingsOption[];
    canExportContactTags: boolean;
    canExportMailjetStatus: boolean;
}

export class ExportRecipientsSettings implements IExportRecipientsSettings {
    public fileName: string;
    public canExportFullName: boolean;
    public canExportCampaignName: boolean;
    public canExportSenderName: boolean;
    public canExportStatusType: boolean;
    public canExportModified: boolean;
    public canExportContactFirstName: boolean;
    public canExportContactLastNamePrefix: boolean;
    public canExportContactLastName: boolean;
    public canExportContactEmail: boolean;
    public canExportContactMobile: boolean;
    public canExportContactPhone: boolean;
    public canExportContactJobTitle: boolean;
    public canExportContactCompany: boolean;
    public canExportContactAccountOwner: boolean;
    public canExportContactLanguage: boolean;
    public canExportContactContactType: boolean;
    public canExportContactGender: boolean;
    public canExportContactSalutation: boolean;
    public canExportContactCrmId: boolean;
    public canExportContactCustomerNumber: boolean;
    public canExportContactAccessCode: boolean;
    public canExportContactSmsPermission: boolean;
    public canExportContactIsActive: boolean;
    public canExportContactAddressLineOne: boolean;
    public canExportContactAddressLineTwo: boolean;
    public canExportContactCity: boolean;
    public canExportContactPostalCode: boolean;
    public canExportContactCountry: boolean;
    public canExportContactCustomFields: string[];
    public canExportContactUnsubscribeOptions: IUnsubscribeSettingsOption[];
    public canExportContactTags: boolean;
    public canExportMailjetStatus: boolean;
}

export interface IAllowedListOfRecipients {
    id: string;
    name: string;
}

export class AllowedListOfRecipients implements IAllowedListOfRecipients {
    constructor(
        public id: string,
        public name: string
    ) { }
}

export interface IUnsubscribeOption {
    id: string;
}

export class UnsubscribeOption implements IUnsubscribeOption {
    constructor(
        public id: string
    ) { }
}

export interface ICampaignTag {
    id: string;
}

export class CampaignTag implements ICampaignTag {
    constructor(
        public id: string
    ) { }
}
